// src/App.jsx
import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { Box, Toolbar, CircularProgress } from '@mui/material';

// Layout & Auth (always needed — kept eager)
import { AuthProvider } from './utils/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Sidebar from './components/Sidebar';
import Navbar from './components/Navbar';

// Public pages (needed immediately — kept eager)
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import CreateAccount from "./pages/CreateAccount";
import ActivityTracker from './components/ActivityTracker';

// Route-level code splitting — each page loads only when navigated to
const Dashboard = React.lazy(() => import('./components/Dashboard'));
const CustomerDetails = React.lazy(() => import('./components/CustomerDetails'));
const Project = React.lazy(() => import('./components/Project'));
const Payment = React.lazy(() => import('./pages/Payment'));
const SampleRegistration = React.lazy(() => import('./pages/SampleRegistration'));
const SamplePreparation = React.lazy(() => import('./pages/SamplePreparation'));
const LibraryPreparation = React.lazy(() => import('./pages/LibraryPreparation'));
const Sequencing = React.lazy(() => import('./pages/Sequencing'));
const BioInformaticsDataAnalysis = React.lazy(() => import('./pages/BioInformaticsDataAnalysis'));
const SamplePreparationReport = React.lazy(() => import('./components/SamplePreparationReport'));
const AdminApproval = React.lazy(() => import('./pages/AdminApproval'));
const ReportsGenerated = React.lazy(() => import('./components/ReportsGenerated'));
const SampleRegistrationList = React.lazy(() => import('./views/sampleRegistrationlist'));
const SamplePreparationView = React.lazy(() => import('./views/SamplePreparationView'));
const CustomerDetailsList = React.lazy(() => import('./views/customerdetailslist'));
const ProjectsView = React.lazy(() => import('./views/ProjectsView'));
const LibraryPreparationview = React.lazy(() => import('./views/LibraryPreparationview'));
const SequencingView = React.lazy(() => import('./views/SequencingView'));
const BioinformaticsView = React.lazy(() => import('./views/BioInformaticsView'));
const CustomerProjectsView = React.lazy(() => import('./views/CustomerProjectsView'));
const Gotoproject = React.lazy(() => import('./views/Gotoproject'));
const ActivityLogs = React.lazy(() => import('./pages/ActivityLogs'));
const UserAccess = React.lazy(() => import('./pages/UserAccess'));
const ForceChangePassword = React.lazy(() => import('./pages/ForceChangePassword'));


// Layout wrapper
const Layout = ({ children }) => {
  const location = useLocation();

  const isPublicPage =
    location.pathname === "/" ||
    location.pathname === "/create-account" ||
    location.pathname === "/forgot-password" ||
    location.pathname === "/change-password";

  // ✅ FIXED: use startsWith / regex to match dynamic routes like /customer-projects/CUST-OO96
  const hideNavbar =
    location.pathname === "/customer-details-list" ||
    location.pathname === "/dashboard" ||
    location.pathname.startsWith("/admin/") ||
    location.pathname.startsWith("/customer-projects/") ||
    location.pathname.startsWith("/customer-project/");

  if (isPublicPage) return <>{children}</>;

  return (
    <Box sx={{ display: 'flex' }}>
      <Sidebar />
      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        {!hideNavbar && <Navbar />}
        {!hideNavbar && <Toolbar />}
        {children}
      </Box>
    </Box>
  );
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <ActivityTracker />
        <Layout>
          <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}><CircularProgress /></Box>}>
            <Routes>

              {/* Public */}
              <Route path="/" element={<Login />} />
              <Route path="/create-account" element={<CreateAccount />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />

              {/* Any logged-in user — forced first-login password change */}
              <Route element={<ProtectedRoute />}>
                <Route path="/change-password" element={<ForceChangePassword />} />
              </Route>

              {/* Admin console — admins only, regardless of permissions */}
              <Route element={<ProtectedRoute adminOnly />}>
                <Route path="/admin/approvals" element={<AdminApproval />} />
                <Route path="/admin/activity" element={<ActivityLogs />} />
                <Route path="/admin/users" element={<UserAccess />} />
              </Route>

              {/* Permission-driven app routes (module resolved from the path by ProtectedRoute) */}
              <Route element={<ProtectedRoute />}>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/projects" element={<ProjectsView />} />
                <Route path="/project" element={<Project />} />
                <Route path="/customerdetails" element={<CustomerDetails />} />
                <Route path="/customer-details-list" element={<CustomerDetailsList />} />
                <Route path="/customer-projects/:customer_id" element={<Gotoproject />} />
                <Route path="/customer-project/:customer_id/:project_id" element={<CustomerProjectsView />} />
                <Route path="/payment" element={<Payment />} />
                <Route path="/reports-generated" element={<ReportsGenerated />} />

                <Route path="/sample-registration" element={<SampleRegistration />} />
                <Route path="/sample-registration-list" element={<SampleRegistrationList />} />

                <Route path="/sample-preparation" element={<SamplePreparation />} />
                <Route path="/sample-preparation-view" element={<SamplePreparationView />} />
                <Route path="/library-preparation" element={<LibraryPreparation />} />
                <Route path="/Library-Preparation-view" element={<LibraryPreparationview />} />
                <Route path="/sample-preparation-report" element={<SamplePreparationReport />} />

                <Route path="/bio-informatics-data-analysis" element={<BioInformaticsDataAnalysis />} />
                <Route path="/Bioinformatics-view" element={<BioinformaticsView />} />
                <Route path="/sequencing" element={<Sequencing />} />
                <Route path="/Sequencing-view" element={<SequencingView />} />
              </Route>

              {/* Unauthorized */}
              <Route path="/unauthorized" element={
                <Box sx={{ p: 5, textAlign: 'center' }}>
                  <h2>Access Denied</h2>
                  <p>You don't have permission to view this page.</p>
                  <p style={{ color: 'gray' }}>Your role does not allow access to this section.</p>
                </Box>
              } />

              <Route path="*" element={<Navigate to="/unauthorized" replace />} />

            </Routes>
          </Suspense>
        </Layout>
      </Router>
    </AuthProvider>
  );
}

export default App;